import { useEffect, useState, useCallback, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'store';
import {
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
  GridToolbar,
  GridInitialState,
  useGridApiRef,
  GridColumnVisibilityModel,
 } from '@mui/x-data-grid-pro';
 import { ResponsiveDataGrid } from 'ui-component/ResponsiveDataGrid';
import { Box, 
  CircularProgress,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import ConfirmationDialog from 'ui-component/ConfirmationDialog';
import { useTheme } from '@mui/material/styles';
import { IUser } from './../../../../types/user.interface';
import { useUsers } from '../../../../hooks/useUsers.hooks';
import { useCurrentOrganization } from './../../../../hooks/useOrganization.hooks'
import { useUser } from '../../../../hooks/useUser.hooks';
import { openSnackbar } from 'store/slices/snackbar';
import TableButton from 'ui-component/TableButton';

export default function UsersDataGrid(props : any) {
  const {role, active} = props;
  const theme = useTheme();
  const usersState = useUsers(role, active);
  const userState = useUser();
  const organizationState = useCurrentOrganization();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false);
  const [showReactivateConfirmation, setShowReactivateConfirmation] = useState(false);
  const [activeUserID, setActiveUserID] = useState(-1);
  const [resendInProgressId, setResendInProgressId] = useState<number | null>(null);

  const apiRef = useGridApiRef();
  const [initialState, setInitialState] = useState<GridInitialState>();

  const saveSnapshot = useCallback(() => {
    if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();
      localStorage.setItem('adminDonorsUserDataGridState', JSON.stringify(currentState));
    }
  }, [apiRef]);

  useLayoutEffect(() => {
    const stateFromLocalStorage = localStorage?.getItem('adminDonorsUserDataGridState');
    setInitialState(stateFromLocalStorage ? JSON.parse(stateFromLocalStorage) : {});

    // handle refresh and navigating away/refreshing
    window.addEventListener('beforeunload', saveSnapshot);

    return () => {
      // in case of an SPA remove the event-listener
      window.removeEventListener('beforeunload', saveSnapshot);
      saveSnapshot();
    };
  }, [saveSnapshot]);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      // Show 'neighbourhood' column if the admin is part of Fresh Routes
      neighbourhood: (organizationState.ready && organizationState?.organization.id == 3) ? true : false,
    });

  const formatDateTime = (value?: string | null) => {
    if (!value) {
      return 'Never';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'Never' : date.toLocaleString();
  };

  const handleResendSetupEmail = async (targetUser: IUser) => {
    if (!userState.resendSetupEmail) {
      return;
    }

    setResendInProgressId(targetUser.id);
    try {
      const data = await userState.resendSetupEmail(targetUser.email);
      dispatch(
        openSnackbar({
          open: true,
          message: data?.message || 'Verification email resent successfully.',
          variant: 'alert',
          alert: { color: 'success' },
          close: false,
        }),
      );
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Unable to send verification email.';
      dispatch(
        openSnackbar({
          open: true,
          message,
          variant: 'alert',
          alert: { color: 'error' },
          close: false,
        }),
      );
    } finally {
      setResendInProgressId(null);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: (params: GridRowParams) => {
        if (active) {
          return [
            <GridActionsCellItem
              icon={
                <Tooltip title="Edit">
                  <EditIcon />
                </Tooltip>
              }
              label="Edit"
              className="textPrimary"
              onClick={() => navigate('/donor/update/' + params.row.id)}
              color="inherit"
            />,
            <GridActionsCellItem
              icon={
                <Tooltip title="Archive">
                  <ArchiveOutlinedIcon />
                </Tooltip>
              }
              label="Archive"
              onClick={() => {
                setActiveUserID(params.row.id as number);
                setShowArchiveConfirmation(true);
              }}
              color="inherit"
            />,
          ];
        }
        return [
          <GridActionsCellItem
            icon={
              <Tooltip title="Reactivate">
                <UnarchiveOutlinedIcon />
              </Tooltip>
            }
            label="Reactivate"
            onClick={() => {
              setActiveUserID(params.row.id as number);
              setShowReactivateConfirmation(true);
            }}
            color="inherit"
          />,
        ];
      },
    },
    { field: 'region_name', headerName: 'Region', width: 100,
    valueGetter: (value, row) =>
      {
        return `${row.region.name}`},
    },
    { field: 'name', headerName: 'Donor Name', width: 350,
    valueGetter: (value, row) =>
      {
        return `${row.first_name + ' ' + row.last_name}`},
    },
    { field: 'email', headerName: 'Email', width: 300 },
    { field: 'phone', headerName: 'Phone Number', width: 200 },
    { field: 'program_name', headerName: 'Program', width: 130 },
    { field: 'neighbourhood', headerName: 'Neighbourhood', width: 130 },
    { field: 'address', headerName: 'Address', width: 130},
    { field: "password_set", headerName: "Password Set", width: 140, 
      valueGetter: (value, row) => (row?.must_setup_password ? "No" : "Yes")
    },
    { field: "email_verified", headerName: "Email Verified", width: 140, 
      valueGetter: (value, row) => ((row?.email_verified || row?.email_verified_at) ? "Yes" : "No")
    },
    { field: 'last_setup_email_sent', headerName: 'Last Setup Email', width: 190,
      valueGetter: (value, row) => formatDateTime(row?.last_setup_email_sent)
    },
    {
      field: 'resend_setup_email',
      headerName: 'Resend Verification',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params: { row: IUser }) => {
        const handleClick = (event: any) => {
          event.stopPropagation();
          handleResendSetupEmail(params.row);
        };

        const isProcessing = resendInProgressId === params.row.id;
        return (
          <TableButton
            onClick={handleClick}
            disabled={!userState.resendSetupEmail || isProcessing}
          >
            {isProcessing ? 'Sending...' : 'Resend Verification'}
          </TableButton>
        );
      },
    },
    {
      field: 'id',
      headerName: 'ID',
      type: 'string',
      width: 100,
      editable: false,
    },
  ];

  function getProgramName(program_id: number, programs: any) {
    let programObj = programs.find((program: any) => program.id === program_id);
    return programObj ? programObj.name : "Not applicable";
  }

  const [rows, setRows] = useState<IUser[]>([]);
  let usersArray : IUser[];
  useEffect(() => {
    if (usersState?.ready && organizationState.ready && !userState.deleteInProgress) {
      usersArray = Object.values(usersState.users).sort((a : any, b : any) => a.first_name.localeCompare(b.first_name));
      usersArray = usersArray.map((user : any)=>{
        user.program_name = getProgramName(user.program_id, organizationState.organization.programs);
        return user;
    })
      setRows(usersArray);
    }
  }, [usersState?.ready, organizationState.ready, userState?.deleteInProgress, usersState.users]);
  
  const onRowsSelectionHandler = (ids: any) => {
    // const selectedRowsData = ids.map((id: any) => rows.find((row) => row.id === id));
    // setUser(selectedRowsData[0]);
  };

  const cancelDialog = () => {
    setShowArchiveConfirmation(false);
    setShowReactivateConfirmation(false);
  }

  const confirmArchive = async () => {
    await userState.deleteUser(activeUserID);
    await usersState.fetch(); // to refresh users
    setShowArchiveConfirmation(false);
  };

  const confirmReactivate = async () => {
    await userState.reactivateUser(activeUserID);
    await usersState.fetch(); // to refresh users
    setShowReactivateConfirmation(false);
  };

  if (!usersState?.ready) {
    return (
      <div>Loading...</div>
  )
  }

  // Handle saved table views
  if (!initialState) {
    return <CircularProgress />;
  }
  
  return (
    <div style={{ height: 700, width: '100%' }}>
      <Box
        sx={{
        height: 700,
        width: '100%',
        '& .actions': {
          color: 'text.secondary',
        },
        '& .textPrimary': {
          color: 'text.primary',
        },
        '& .MuiDataGrid-root': {
            border: 'none',
            '& .MuiDataGrid-cell': {
                borderColor: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
            },
            '& .MuiDataGrid-columnsContainer': {
                color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900',
                borderColor: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
            },
            '& .MuiDataGrid-columnSeparator': {
                color: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
            }
          },
          '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
            borderRight: `1px solid ${
              theme.palette.mode === 'light' ? '#f0f0f0' : '#303030'
            }`,
          },
      }}
      >
      <ResponsiveDataGrid 
      rows={rows} 
      columns={columns}
      pagination
      slots={{ toolbar: GridToolbar }}
      onRowSelectionModelChange={(ids : any) => onRowsSelectionHandler(ids)}
      apiRef={apiRef}
      initialState={{
        ...initialState,
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={(newModel) =>
        setColumnVisibilityModel(newModel)
      }
      />
      <ConfirmationDialog
          open={showArchiveConfirmation}
          onCancel={cancelDialog}
          onConfirm={confirmArchive}
          title="Archive User?"
          message="Confirm you want to archive this user?"
      />
      <ConfirmationDialog
          open={showReactivateConfirmation}
          onCancel={cancelDialog}
          onConfirm={confirmReactivate}
          title="Reactivate User?"
          message="Confirm you want to reactivate this user?"
      />
      </Box>
    </div>
    
  );
}
